import {
  expandEffectiveAbsenceDays,
  listAbsenceRequests,
  type AbsenceDayMarker,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import {
  calendarDaysToMap,
  getCalendarDays,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  getEmployeeMonthSummary,
  getEmployeeTimeProfile,
  type EmployeeTimeEmployeeProfile,
  type EmployeeTimeMonthSummary,
} from "@/lib/employeeTimes";
import {
  expandHeroAbsenceDays,
  getHeroEmployeeCalendar,
  heroHoursByDayMap,
} from "@/lib/heroEmployeeHr";

interface YearMeta {
  absences: EmployeeAbsenceRequest[];
  calendarByDay: Map<string, EmployeeCalendarDay>;
  absenceByDay: Map<string, AbsenceDayMarker[]>;
}

interface HeroYearMeta {
  hoursByDay: Map<string, number>;
  absenceByDay: Map<string, AbsenceDayMarker[]>;
  linked: boolean;
}

const yearMetaCache = new Map<string, YearMeta>();
const heroYearCache = new Map<string, HeroYearMeta | null>();
const monthSummaryCache = new Map<string, EmployeeTimeMonthSummary>();
const profileCache = new Map<string, EmployeeTimeEmployeeProfile>();
const heroInflight = new Map<string, Promise<HeroYearMeta | null>>();

function yearKey(login: string, year: number): string {
  return `${login.trim().toLowerCase()}|${year}`;
}

function monthKey(login: string, year: number, month: number): string {
  return `${login.trim().toLowerCase()}|${year}|${month}`;
}

export function invalidateCaptureCalendarCache(login?: string): void {
  if (!login?.trim()) {
    yearMetaCache.clear();
    heroYearCache.clear();
    monthSummaryCache.clear();
    heroInflight.clear();
    return;
  }
  const prefix = `${login.trim().toLowerCase()}|`;
  for (const key of [...yearMetaCache.keys()]) {
    if (key.startsWith(prefix)) yearMetaCache.delete(key);
  }
  for (const key of [...heroYearCache.keys()]) {
    if (key.startsWith(prefix)) heroYearCache.delete(key);
  }
  for (const key of [...monthSummaryCache.keys()]) {
    if (key.startsWith(prefix)) monthSummaryCache.delete(key);
  }
  for (const key of [...heroInflight.keys()]) {
    if (key.startsWith(prefix)) heroInflight.delete(key);
  }
}

async function loadYearMeta(login: string, year: number): Promise<YearMeta> {
  const key = yearKey(login, year);
  const cached = yearMetaCache.get(key);
  if (cached) return cached;

  const [absences, calendar] = await Promise.all([
    listAbsenceRequests({ user_login: login, year }),
    getCalendarDays({ user_login: login, year }),
  ]);
  const calendarByDay = calendarDaysToMap(calendar.days);
  const absenceByDay = expandEffectiveAbsenceDays(absences, calendarByDay, {
    year,
    includePending: true,
  });
  const meta: YearMeta = { absences, calendarByDay, absenceByDay };
  yearMetaCache.set(key, meta);
  return meta;
}

async function loadMonthSummaryLite(
  login: string,
  year: number,
  month: number,
): Promise<EmployeeTimeMonthSummary> {
  const key = monthKey(login, year, month);
  const cached = monthSummaryCache.get(key);
  if (cached) return cached;

  const summary = await getEmployeeMonthSummary({
    user_login: login,
    year,
    month,
    lite: true,
  });
  monthSummaryCache.set(key, summary);
  return summary;
}

/** Schneller App-Monat (ohne Hero) für sofortige Anzeige. */
export async function loadCaptureCalendarMonth(params: {
  userLogin: string;
  year: number;
  month: number;
}): Promise<{
  summary: EmployeeTimeMonthSummary;
  calendarByDay: Map<string, EmployeeCalendarDay>;
  absenceByDay: Map<string, AbsenceDayMarker[]>;
}> {
  const login = params.userLogin.trim();
  const [summary, yearMeta] = await Promise.all([
    loadMonthSummaryLite(login, params.year, params.month),
    loadYearMeta(login, params.year),
  ]);
  return {
    summary,
    calendarByDay: yearMeta.calendarByDay,
    absenceByDay: yearMeta.absenceByDay,
  };
}

/** Hero-Jahr im Hintergrund; Ergebnis wird gecacht. */
export function loadCaptureCalendarHero(params: {
  userLogin: string;
  year: number;
}): Promise<HeroYearMeta | null> {
  const login = params.userLogin.trim();
  const key = yearKey(login, params.year);
  if (heroYearCache.has(key)) {
    return Promise.resolve(heroYearCache.get(key) ?? null);
  }
  const inflight = heroInflight.get(key);
  if (inflight) return inflight;

  const promise = getHeroEmployeeCalendar({ year: params.year, user_login: login })
    .then((hero) => {
      if (!hero.linked) {
        heroYearCache.set(key, null);
        return null;
      }
      const meta: HeroYearMeta = {
        hoursByDay: heroHoursByDayMap(hero.hours_by_day),
        absenceByDay: expandHeroAbsenceDays(hero.absences),
        linked: true,
      };
      heroYearCache.set(key, meta);
      return meta;
    })
    .catch(() => {
      heroYearCache.set(key, null);
      return null;
    })
    .finally(() => {
      heroInflight.delete(key);
    });

  heroInflight.set(key, promise);
  return promise;
}

export async function getCachedEmployeeTimeProfile(
  login: string,
): Promise<EmployeeTimeEmployeeProfile> {
  const key = login.trim().toLowerCase();
  const cached = profileCache.get(key);
  if (cached) return cached;
  const profile = await getEmployeeTimeProfile(login);
  profileCache.set(key, profile);
  return profile;
}
